/**
 * GlobalSearch — Sprint 6.1 (UX/UI Refactor NOC).
 *
 * Busca global no estilo command palette. Abre com Ctrl/Cmd+K ou
 * pelo campo de busca do Topbar. Filtra todas as views navegáveis
 * pelo nome e salta direto para a escolhida.
 *
 * Navegação por teclado: ↑/↓ move, Enter seleciona, Esc fecha.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import NavIcon from "../../nav/NavIcon.jsx";
import { ALL_NAV_ITEMS } from "../../nav/navConfig.js";
import { navItemVisible } from "../../lib/rbac.js";
import { t } from "../../i18n/index.js";

export default function GlobalSearch({ open, onClose, onNavigate }) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  // Foca o input ao abrir; limpa ao fechar.
  useEffect(() => {
    if (open) {
      setQuery("");
      setCursor(0);
      // pequeno delay para o input já estar montado
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Resultados — filtra os itens de navegação pelo label.
  const results = useMemo(() => {
    // S3.9E — só mostra na busca as views permitidas à role atual.
    const items = ALL_NAV_ITEMS.filter(navItemVisible).map((it) => ({
      ...it,
      label: t(it.labelKey),
      group: t(it.groupKey),
    }));
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        it.group.toLowerCase().includes(q) ||
        it.view.toLowerCase().includes(q)
    );
  }, [query]);

  // Mantém o cursor dentro do range.
  useEffect(() => {
    if (cursor >= results.length) setCursor(Math.max(0, results.length - 1));
  }, [results, cursor]);

  if (!open) return null;

  function handleKey(e) {
    if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const sel = results[cursor];
      if (sel) {
        onNavigate(sel.view);
        onClose();
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center
                 pt-[12vh] px-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg ipwd-glass rounded-xl overflow-hidden
                   shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Campo de busca */}
        <div className="flex items-center gap-3 px-4 py-3 border-b
                        border-noc-border">
          <NavIcon name="search" size={18}
                   className="text-noc-muted shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder={t("shell.searchPlaceholder")}
            className="flex-1 bg-transparent text-noc-text text-sm
                       placeholder:text-noc-muted/60 outline-none"
          />
          <kbd className="text-[10px] text-noc-muted border
                          border-noc-border rounded px-1.5 py-0.5">
            ESC
          </kbd>
        </div>

        {/* Resultados */}
        <div className="max-h-[50vh] overflow-y-auto py-1.5">
          {results.length === 0 && (
            <div className="px-4 py-6 text-center text-sm
                            text-noc-muted/70">
              {t("shell.searchEmpty")}
            </div>
          )}
          {results.map((it, idx) => (
            <button
              key={it.view}
              onClick={() => { onNavigate(it.view); onClose(); }}
              onMouseEnter={() => setCursor(idx)}
              className={
                "w-full flex items-center gap-3 px-4 py-2 text-left "
                + "transition-colors "
                + (idx === cursor
                    ? "bg-noc-accent/15 text-noc-text"
                    : "text-noc-muted hover:bg-noc-panel2")
              }
            >
              <NavIcon
                name={it.icon}
                size={16}
                className={idx === cursor
                  ? "text-noc-accent" : "text-noc-muted"}
              />
              <span className="flex-1 text-sm">{it.label}</span>
              <span className="text-[10px] text-noc-muted/60
                               uppercase tracking-wide">
                {it.group}
              </span>
            </button>
          ))}
        </div>

        {/* Rodapé com dicas de teclado */}
        <div className="flex items-center gap-3 px-4 py-2 border-t
                        border-noc-border text-[10px] text-noc-muted/70">
          <span className="flex items-center gap-1">
            <kbd className="border border-noc-border rounded px-1">↑</kbd>
            <kbd className="border border-noc-border rounded px-1">↓</kbd>
            {t("shell.kbdNavigate")}
          </span>
          <span className="flex items-center gap-1">
            <kbd className="border border-noc-border rounded px-1">↵</kbd>
            {t("shell.kbdSelect")}
          </span>
        </div>
      </div>
    </div>
  );
}
